import isEqual from 'lodash/isEqual';

import {createCheckboxes, createField, createInputField, disableFormControls} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';

const defaultRoutingType = 'UnConditional';

const normalizeString = value => typeof value === 'undefined' || value === null ? '' : String(value);

const normalizeRoute = route => {
    if (!isPlainObject(route)) {
        return {
            name: '',
            routingType: defaultRoutingType,
            condition: null
        };
    }

    return {
        ...cloneValue(route),
        name: normalizeString(route.name),
        routingType: route.routingType || defaultRoutingType,
        condition: typeof route.condition === 'undefined' ? null : route.condition
    };
};

const getResource = (mxResources, key) => mxResources.get(key);

const createRoutingConditionMount = () => {
    const routingConditionEditor = document.createElement('div');
    routingConditionEditor.id = 'routingConditionEditor';
    routingConditionEditor.className = 'ui active centered inline loader';
    return routingConditionEditor;
};

const createRoutingForm = ({
                               route = {},
                               routeErrors = {},
                               editable = true,
                               mxResources,
                               hasUnconditional = false,
                               conditionalDisabled = false,
                               showRoutingType = true,
                               onRouteChange = () => {}
                           } = {}) => {
    let routing = normalizeRoute(route);

    const container = document.createElement('div');
    container.id = 'routingEditor';
    container.className = 'ui form';

    const routingTypeSlot = document.createElement('div');
    routingTypeSlot.className = 'routing-type-fields';
    const conditionSlot = document.createElement('div');
    conditionSlot.className = 'routing-condition-field';

    const nameField = createInputField({
        label: getResource(mxResources, 'name'),
        id: 'name',
        name: 'name',
        type: 'text',
        value: routing.name
    });
    const nameInput = nameField.querySelector('input');
    nameInput.addEventListener('change', event => emitRoute({name: event.target.value}));

    container.appendChild(nameField);
    container.appendChild(routingTypeSlot);
    container.appendChild(conditionSlot);

    const renderRoutingType = () => {
        routingTypeSlot.replaceChildren();
        conditionSlot.replaceChildren();

        if (!showRoutingType) {
            return;
        }

        const routingTypeFields = createCheckboxes({
            label: getResource(mxResources, 'conditionalRouting'),
            name: 'routingType',
            value: routing.routingType,
            type: 'radio',
            error: routeErrors?.fields?.routingType,
            className: 'required',
            options: [{
                label: getResource(mxResources, 'alwaysTrue'),
                className: 'inline',
                help: hasUnconditional ? getResource(mxResources, 'alwaysTrueDisabled') : null,
                value: 'UnConditional',
                disabled: hasUnconditional,
                onChange: () => emitRoute({
                    routingType: 'UnConditional',
                    condition: null
                })
            }, {
                label: getResource(mxResources, 'condition'),
                className: 'inline',
                help: getResource(mxResources, 'conditionHelp'),
                value: 'Conditional',
                disabled: conditionalDisabled,
                onChange: () => emitRoute({routingType: 'Conditional'})
            }]
        });

        routingTypeSlot.appendChild(routingTypeFields);

        if (routing.routingType === 'Conditional') {
            conditionSlot.appendChild(createRoutingConditionMount());
        }
    };

    const syncStructuredFields = () => {
        nameInput.value = routing.name;
        renderRoutingType();
    };

    function emitRoute(patch) {
        const nextRouting = normalizeRoute({
            ...routing,
            ...patch
        });

        if (isEqual(routing, nextRouting)) {
            return;
        }

        routing = nextRouting;
        Object.assign(route, patch);
        onRouteChange(cloneValue(patch), cloneValue(routing));
        renderRoutingType();
    }

    renderRoutingType();

    if (!editable) {
        disableFormControls(container);
    }

    return {
        element: container,
        getRoute: () => cloneValue(routing),
        setRoute: value => {
            routing = normalizeRoute(value);
            Object.assign(route, cloneValue(routing));
            syncStructuredFields();
        },
        mountConditionEditor: fieldElement => {
            if (routing.routingType !== 'Conditional') {
                return;
            }

            conditionSlot.replaceChildren(createField({
                id: 'routingConditionEditor',
                error: routeErrors?.fields?.condition,
                fieldElement
            }));
        }
    };
};

export {
    createRoutingConditionMount,
    createRoutingForm,
    defaultRoutingType,
    normalizeRoute
};
